<template>
    <vx-modal :visible="visible" @update:visible="updateVisible($event)" size="lg" padding="standard">
        <template v-slot:header>
            Assign Schedules
        </template>

        <template v-slot:subheader>
            Choose one or more Calendly schedules used by <strong class="font-bold">{{ groupName }}</strong>.
        </template>

        <div class="flex flex-col gap-4">
            <page-loading v-if="loadingEventTypes"></page-loading>

            <template v-else-if="connectionError">
                <vx-alert color="info" :closeable="false">
                    <template v-slot:icon>
                        <font-awesome-icon widthAuto :icon="faCircleExclamation" />
                    </template>
                    <div class="flex flex-col gap-2">
                        <div>Unable to load event types. Please check your Calendly connection.</div>
                        <a :href="`/settings/integrations#/calendly`" class="text-amber-700 hover:text-amber-800 underline">
                            Check Calendly connection
                        </a>
                    </div>
                </vx-alert>
            </template>

            <template v-else-if="availableEventTypes.length === 0">
                <vx-alert color="info" :closeable="false">
                    <template v-slot:icon>
                        <font-awesome-icon widthAuto :icon="faCircleInfo" />
                    </template>
                    No active event types found in your Calendly account. Create event types in Calendly first, then refresh this page.
                </vx-alert>
            </template>

            <template v-else>
                <div class="flex flex-col gap-3 max-h-96 overflow-y-auto">
                    <div
                        v-for="eventType in availableEventTypes"
                        :key="eventType.event_type_uri"
                        data-test="event-type-card"
                        class="flex items-center gap-3 p-4 border rounded-xl transition-colors"
                        :class="[
                            isEventTypeMappedToOtherGroup(eventType)
                                ? 'border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed'
                                : selectedEventTypeUris.includes(eventType.event_type_uri)
                                    ? 'border-sky-200 bg-sky-50 hover:bg-sky-100/50 cursor-pointer'
                                    : 'border-slate-100 bg-white hover:bg-sky-50/50 cursor-pointer'
                        ]"
                        @click="!isEventTypeMappedToOtherGroup(eventType) && toggleEventType(eventType.event_type_uri)"
                        @keydown.space.prevent="!isEventTypeMappedToOtherGroup(eventType) && toggleEventType(eventType.event_type_uri)"
                        @keydown.enter.prevent="!isEventTypeMappedToOtherGroup(eventType) && toggleEventType(eventType.event_type_uri)"
                    >
                        <vx-checkbox
                            :modelValue="selectedEventTypeUris.includes(eventType.event_type_uri)"
                            :disabled="isEventTypeMappedToOtherGroup(eventType)"
                            :aria-label="eventType.name"
                        ></vx-checkbox>
                        <div class="flex-1 flex flex-col gap-1">
                            <div class="flex items-center gap-2">
                                <span class="text-sm font-semibold" aria-hidden="true" data-test="event-type-name">{{ eventType.name }}</span>
                                <span v-if="isEventTypeMappedToOtherGroup(eventType)" class="text-xs text-slate-500 italic">
                                    (Mapped to another group)
                                </span>
                            </div>
                            <span class="text-xs text-slate-500 flex items-center gap-1">
                                <font-awesome-icon :icon="faCalendarClock" widthAuto />
                                {{ eventType.duration }} min • {{ eventType.scheduling_url }}
                            </span>
                        </div>
                    </div>
                </div>

                <div v-if="hasMore" class="mt-4">
                    <vx-button
                        @click="loadMore"
                        color="muted-dark"
                        size="md"
                        class="grow-0"
                        :loading="loadingMore"
                    >
                        Load More
                        <font-awesome-icon widthAuto :icon="faSpinner" />
                    </vx-button>
                </div>
            </template>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button
                    @click="updateVisible(false)"
                    type="button"
                    class="grow lg:w-20 lg:grow-0"
                    size="lg"
                    color="muted"
                >
                    Cancel
                </vx-button>
                <vx-button
                    @click.prevent="submit()"
                    :loading="submitting"
                    type="button"
                    class="grow"
                    size="lg"
                    color="primary"
                >
                    Save
                    <font-awesome-icon widthAuto :icon="faCircleCheck" />
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, type PropType } from 'vue';
import { VxAlert, VxButton, VxCheckbox, VxModal } from '@voxie/frontend-components';
import { faCircleCheck, faCircleInfo, faCircleExclamation, faCalendarClock, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import PageLoading from '~/components/general/PageLoading.vue';
import { useToasted } from '~/composables/useToasted';
import { useGroupDetails } from '../useGroupDetails';
import { calendlyClient } from '~/services';
import type { CalendlyEventType } from '~/services/calendly/calendlyClient.types';
import { catchRequestError } from '~/utils/request';

const toasted = useToasted();
const { group } = useGroupDetails();
const groupName = ref(group.value?.name || '');

const teamId = Spark.state.currentTeam.id;

const visible = defineModel('visible', {
    type: Boolean,
    default: false,
});

const props = defineProps({
    groupId: {
        type: String,
        required: true,
    },
    currentEventTypes: {
        type: Array as PropType<CalendlyEventType[]>,
        default: () => [],
    },
});

const emit = defineEmits(['saved']);

const availableEventTypes = ref<CalendlyEventType[]>([]);
const selectedEventTypeUris = ref<string[]>([]);
const loadingEventTypes = ref<boolean>(true);
const loadingMore = ref<boolean>(false);
const submitting = ref<boolean>(false);
const nextPageToken = ref<string | null>(null);
const hasMore = ref<boolean>(false);
const connectionError = ref<boolean>(false);

const updateVisible = (value: boolean) => {
    visible.value = value;
};

const isEventTypeMappedToOtherGroup = (eventType: CalendlyEventType): boolean => {
    return eventType.group_id !== null && eventType.group_id !== undefined && eventType.group_id !== props.groupId;
};

const toggleEventType = (uri: string) => {
    const index = selectedEventTypeUris.value.indexOf(uri);
    if (index > -1) {
        selectedEventTypeUris.value.splice(index, 1);
    } else {
        selectedEventTypeUris.value.push(uri);
    }
};

const loadEventTypes = async (pageToken: string | null = null) => {
    if (pageToken) {
        loadingMore.value = true;
    } else {
        loadingEventTypes.value = true;
        connectionError.value = false;
    }

    try {
        const params = pageToken ? { page_token: pageToken } : undefined;
        const response = await calendlyClient.getEventTypes({ teamId }, params);

        if (pageToken) {
            availableEventTypes.value = [...availableEventTypes.value, ...response.data.event_types];
        } else {
            availableEventTypes.value = response.data.event_types;
        }

        nextPageToken.value = response.data.pagination?.next_page_token || null;
        hasMore.value = !!nextPageToken.value;
    } catch (error: unknown) {
        connectionError.value = true;
        toasted.error(catchRequestError(error));
    } finally {
        loadingEventTypes.value = false;
        loadingMore.value = false;
    }
};

const loadMore = () => {
    if (nextPageToken.value) {
        loadEventTypes(nextPageToken.value);
    }
};

const submit = async () => {
    submitting.value = true;

    try {
        const eventTypes = selectedEventTypeUris.value.map((uri: string) => {
            const eventType = availableEventTypes.value.find((et: CalendlyEventType) => et.event_type_uri === uri);
            return {
                event_type_uri: eventType!.event_type_uri,
                name: eventType!.name,
                scheduling_url: eventType!.scheduling_url,
                duration: eventType!.duration,
            };
        });

        await calendlyClient.updateGroupMapping(
            { teamId },
            props.groupId,
            { event_types: eventTypes }
        );

        toasted.success('Calendly schedules updated successfully');
        emit('saved');
        updateVisible(false);
    } catch (error: unknown) {
        toasted.error(catchRequestError(error));
    } finally {
        submitting.value = false;
    }
};

watch(visible, (newValue: boolean) => {
    if (newValue) {
        selectedEventTypeUris.value = props.currentEventTypes.map((et: CalendlyEventType) => et.event_type_uri);
        loadEventTypes();
    } else {
        availableEventTypes.value = [];
        nextPageToken.value = null;
        hasMore.value = false;
        connectionError.value = false;
    }
});

onMounted(() => {
    if (group.value) {
        groupName.value = group.value.name;
    }
});
</script>
